import { useState } from "react";
import Buttons from "../../../components/Buttons/Buttons";
import { customInstance } from "../../../api/custom-instance";
import { ASSIGNABLE_ROLES } from "./constants";

export default function CreateUserModal({ isOpen, onClose, onSuccess, setPopupMessage }: {
    isOpen: boolean,
    onClose: () => void,
    onSuccess: () => void,
    setPopupMessage: (msg: string, success: boolean) => void,
}) {
    const [username, setUsername] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const resetForm = () => {
        setUsername(''); setFirstName(''); setLastName('');
        setEmail(''); setPassword(''); setSelectedRoles([]);
    };

    const handleSubmit = async () => {
        if (!username || !firstName || !lastName || !email || !password) return;
        setIsSubmitting(true);
        try {
            await customInstance({
                url: 'users/',
                method: 'POST',
                data: {
                    username,
                    first_name: firstName,
                    last_name: lastName,
                    email,
                    password,
                    initial_roles: selectedRoles,
                },
            });
            setPopupMessage(`User '${username}' created successfully.`, true);
            resetForm();
            onSuccess();
            onClose();
        } catch (err: any) {
            const detail = err?.response?.data?.detail || 'Failed to create user.';
            setPopupMessage(detail, false);
        }
        setIsSubmitting(false);
    };

    if (!isOpen) return null;

    const isFormValid = username && firstName && lastName && email && password;

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg w-full max-w-[500px] p-6 flex flex-col gap-4">
                <h2 className="text-lg font-semibold text-default">Create New User</h2>
                <div className="flex flex-col gap-3">
                    <input className="border border-outline rounded px-3 py-2 text-sm" placeholder="Username *" value={username} onChange={e => setUsername(e.target.value)} />
                    <input className="border border-outline rounded px-3 py-2 text-sm" placeholder="Email *" value={email} onChange={e => setEmail(e.target.value)} />
                    <div className="flex gap-2">
                        <input className="border border-outline rounded px-3 py-2 text-sm flex-1" placeholder="First Name *" value={firstName} onChange={e => setFirstName(e.target.value)} />
                        <input className="border border-outline rounded px-3 py-2 text-sm flex-1" placeholder="Last Name *" value={lastName} onChange={e => setLastName(e.target.value)} />
                    </div>
                    <input className="border border-outline rounded px-3 py-2 text-sm" type="password" placeholder="Password *" value={password} onChange={e => setPassword(e.target.value)} />
                    <div>
                        <p className="text-sm font-medium text-default mb-1">Initial Roles</p>
                        <div className="flex flex-wrap gap-2">
                            {ASSIGNABLE_ROLES.map(role => (
                                <label key={role} className="flex items-center gap-1 text-sm cursor-pointer">
                                    <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => setSelectedRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role])} />
                                    {role}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="flex justify-end gap-2 mt-2">
                    <Buttons id="cancel-create-user" type="tertiary" styling="outline" label="Cancel" action={onClose} />
                    <Buttons id="submit-create-user" type={(!isFormValid || isSubmitting) ? 'disabled' : 'primary'} styling="solid" label={isSubmitting ? 'Creating...' : 'Create User'} action={handleSubmit} />
                </div>
            </div>
        </div>
    );
}
